/**
 * Short label for image file type badges (PNG, SVG, …) from a file name.
 */
export function getImageTypeLabel(fileName: string): string {
  if (!fileName || typeof fileName !== 'string') {
    return 'IMG'
  }
  const dot = fileName.lastIndexOf('.')
  if (dot < 0 || dot === fileName.length - 1) {
    return 'IMG'
  }
  const ext = fileName.slice(dot + 1).toLowerCase()
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'JPG'
    case 'png':
      return 'PNG'
    case 'svg':
      return 'SVG'
    case 'webp':
      return 'WEBP'
    case 'gif':
      return 'GIF'
    case 'ico':
      return 'ICO'
    case 'avif':
      return 'AVIF'
    default: {
      const u = ext.toUpperCase()
      return u.length <= 10 ? u : `${u.slice(0, 8)}…`
    }
  }
}

/**
 * Download an image URL as a file.
 * Use `credentials: 'include'` for Worker URLs behind Cloudflare Access; `omit` for public CDN/R2 URLs.
 * If `fetch` fails (e.g. CORS or network), opens the URL in a new tab so the user can save manually.
 *
 * @param url - Absolute URL to the image
 * @param fileName - Suggested file name
 */
export async function downloadImageAsFile(
  url: string,
  fileName: string,
  options: { credentials?: RequestCredentials } = {}
): Promise<'blob' | 'fallback'> {
  const credentials = options.credentials ?? 'include'
  try {
    const response = await fetch(url, { credentials })
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`)
    }
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    try {
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = fileName || 'image'
      anchor.rel = 'noopener'
      anchor.style.display = 'none'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
    return 'blob'
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer')
    return 'fallback'
  }
}
